import MailOpenedIcon from "@assets/icons/mail-opened.svg";
import SettingsIcon from "@assets/icons/settings.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { notificationManager } from "@core/service-container";
import { Notification } from "@domain/notification/notification";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { Page } from "@ui/components/common/page";
import { PageHeader } from "@ui/components/common/page-header";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { EmptyNotificationList } from "@ui/pages/notification-center/components/empty-notification-list";
import { NotificationRow } from "@ui/pages/notification-center/components/notification-row";
import { NotificationSection } from "@ui/pages/notification-center/components/notification-section";
import { TabSettings } from "@ui/pages/settings/settings-page";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import { datesAreOnSameDay, now, WEEK_LENGTH } from "@utils/date-utils";
import { Dayjs } from "dayjs";
import { useObservable } from "micro-observables";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ValidateAccountCard } from "@ui/pages/home/components/validate-account-card";

interface NotificationGroup {
	id: string;
	title: string;
	notifications: Notification[];
	predicate: (date: Dayjs) => boolean;
}

export const NotificationCenterPage = (): JSX.Element => {
	const { format } = useI18n();
	const { isDesktop, isMobile } = useDimensions();
	const notifications = useObservable(notificationManager.notifications);
	const unreadNotificationCount = useObservable(notificationManager.unreadCount);
	const [hasReachedEnd, setHasReachedEnd] = useState(false);
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);

	const disabled = useMemo(() => unreadNotificationCount === 0, [unreadNotificationCount]);

	const openMarkAllAsReadDialog = useCallback(
		() =>
			dialogContext?.openModal(ModalId.MarkAllNotificationsAsRead, {
				unreadNotificationCount,
			}),
		[dialogContext, unreadNotificationCount],
	);

	const [loadNotification, isLoading] = useAsyncOperation(
		useCallback(async () => {
			const { reachedEnd } = await notificationManager.loadNotifications();
			setHasReachedEnd(reachedEnd);
		}, []),
		{},
	);

	const [loadMoreNotifications, isPageLoading] = useAsyncOperation(
		useCallback(async () => {
			const { reachedEnd } = await notificationManager.loadMoreNotifications();
			setHasReachedEnd(reachedEnd);
		}, []),
		{},
	);

	useEffect(() => {
		loadNotification();
	}, [loadNotification]);

	const notificationGroups = useMemo(() => {
		const groups: NotificationGroup[] = [
			{
				id: "today",
				notifications: [],
				title: format("notificationCenter.sections.today"),
				predicate: date => datesAreOnSameDay(date, now()),
			},
			{
				id: "last7days",
				notifications: [],
				title: format("notificationCenter.sections.last7days"),
				predicate: date => now().diff(date, "days", true) < WEEK_LENGTH,
			},
			{
				id: "last30days",
				notifications: [],
				title: format("notificationCenter.sections.last30days"),
				predicate: date => now().diff(date, "days", true) < 30,
			},
			{
				id: "older",
				notifications: [],
				title: format("notificationCenter.sections.older"),
				predicate: () => true,
			},
		];

		notifications.forEach(notification => {
			for (const group of groups) {
				if (group.predicate(notification.creationDate)) {
					group.notifications.push(notification);
					return;
				}
			}
		});

		return groups.filter(group => group.notifications.length > 0);
	}, [notifications, format]);

	useEffect(() => {
		if (isLoading || isPageLoading || hasReachedEnd || isNil(bottomListDivRef.current)) return;
		const intersectionObserver = new IntersectionObserver(async ([elem]) => {
			if (elem.isIntersecting) {
				await loadMoreNotifications();
			}
		});
		intersectionObserver.observe(bottomListDivRef.current);
		return () => intersectionObserver.disconnect();
	}, [isLoading, isPageLoading, hasReachedEnd, loadMoreNotifications]);

	const bottomListDivRef = useRef<HTMLDivElement>(null);

	const navigateToSettings = useCallback(
		() => navigate(RoutesPath.Settings, { query: { active: TabSettings.NotificationPreferences } }),
		[navigate],
	);

	const renderContent = () => {
		if (isLoading) {
			return <StyledLoadingContainer />;
		} else if (notificationGroups.length === 0) {
			return <EmptyNotificationList />;
		}

		return (
			<>
				{notificationGroups.map(group => (
					<NotificationSection key={group.id} title={group.title}>
						{group.notifications.map((notification, index) => (
							<NotificationRow
								notification={notification}
								key={notification.id}
								separator={index !== group.notifications.length - 1}
							/>
						))}
					</NotificationSection>
				))}
				{isPageLoading && <LoadingContainer />}
				<div ref={bottomListDivRef} />
			</>
		);
	};

	return (
		<>
			<Page>
				<ValidateAccountCard showForPendingPt={false} />
				<PageHeader
					title={format("notificationCenter.title")}
					tertiaryButton={
						!isMobile
							? {
									label: format("settings.notificationPreferences"),
									onClick: navigateToSettings,
									icon: "pi pi-cog",
								}
							: undefined
					}
					menuItems={
						isMobile
							? [
									{
										icon: <MailOpenedIcon color={colors.primary} />,
										text: format("notificationCenter.markAllAsRead.button"),
										disabled,
										onClick: openMarkAllAsReadDialog,
										itemClassName: "p-size-s-bold",
									},
									{
										icon: <SettingsIcon color={colors.primary} />,
										text: format("settings.notificationPreferences"),
										onClick: navigateToSettings,
										itemClassName: "p-size-s-bold",
									},
								]
							: undefined
					}
				/>
				<Card className="p-card">
					{isDesktop && (
						<ButtonContainer>
							<Button
								disabled={disabled}
								className="p-size-s-bold"
								onClick={() => !disabled && openMarkAllAsReadDialog()}
							>
								{format("notificationCenter.markAllAsRead.button")} <MailOpenedIcon width={16} height={16} />
							</Button>
						</ButtonContainer>
					)}
					{renderContent()}
				</Card>
			</Page>
		</>
	);
};

const StyledLoadingContainer = styled(LoadingContainer)`
	margin-top: 128px;
	margin-bottom: 128px;
`;

const Card = styled.div`
	padding: 32px;
	width: 1200px;
	max-width: 100%;
`;

const ButtonContainer = styled.div`
	${row(undefined, "spacebetween")}
`;

const Button = styled.div<{ disabled?: boolean }>`
	${row(undefined, "center", "center")};
	gap: 8px;

	${({ disabled }) =>
		disabled
			? css`
					color: ${colors.grey50};
				`
			: css`
					cursor: pointer;

					&:hover {
						color: ${colors.primary};
					}

					&:active {
						color: ${colors.primaryDark};
					}
				`}
`;
